import React, { useRef } from 'react';
import './ResumePreview.css';

const ResumePreview = ({ resumeData, technicalSkills = [], projects = [], certifications = [], onBack, onDownloadPDF, isLoading }) => {
  const componentRef = useRef();

  // Group only selected skills by category
  const getSkillsByCategory = () => {
    const grouped = {};
    if (!resumeData.technicalSkillIds || !Array.isArray(resumeData.technicalSkillIds)) return grouped;
    technicalSkills.filter(skill => resumeData.technicalSkillIds.includes(skill.id)).forEach(skill => {
      if (!skill || !skill.category) return;
      if (!grouped[skill.category]) grouped[skill.category] = [];
      grouped[skill.category].push(skill.name);
    });
    return grouped;
  };

  // Helper for rendering contact links
  const renderContactLinks = () => (
    <span style={{ fontSize: 15 }}>
      {resumeData.mobile && <span>{resumeData.mobile}</span>}
      {resumeData.email && <span> | <a href={`mailto:${resumeData.email}`} style={{ color: '#1976d2', textDecoration: 'underline' }}>{resumeData.email}</a></span>}
      {resumeData.linkedin && resumeData.linkedin.trim() && <span> | <a href={resumeData.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>LinkedIn</a></span>}
      {resumeData.github && resumeData.github.trim() && <span> | <a href={resumeData.github} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>GitHub</a></span>}
    </span>
  );

  // Helper for rendering grouped skills
  const renderSkills = () => {
    const grouped = getSkillsByCategory();
    if (Object.keys(grouped).length === 0) return null;
    return (
      <ul style={{ margin: '8px 0 0 18px', fontSize: 15, listStyle: 'none', padding: 0 }}>
        {Object.entries(grouped).map(([category, names]) => (
          <li key={category} style={{ marginBottom: 2 }}>
            <b>{category}:</b> {names.join(', ')}
          </li>
        ))}
      </ul>
    );
  };

  // Helper for rendering profile summary (HTML or bullets/paragraph)
  const renderProfileSummary = () => {
    if (!resumeData.profileSummary) return null;
    const summary = resumeData.profileSummary.trim();
    if (/<ul>|<li>|<ol>/.test(summary)) {
      return <div style={{ fontSize: 15 }} dangerouslySetInnerHTML={{ __html: summary }} />;
    }
    // Detect bullet points or line breaks
    const lines = summary.split(/\r?\n|•/).map(l => l.trim()).filter(Boolean);
    if (lines.length > 1) {
  return (
        <ul style={{ margin: '8px 0 0 18px', fontSize: 15 }}>
          {lines.map((line, idx) => <li key={idx}>{line}</li>)}
        </ul>
      );
    }
    return <div style={{ fontSize: 15 }}>{summary}</div>;
  };

  // Helper for rendering experience as bullet points
  const renderExperience = () => (
    resumeData.workExperiences && resumeData.workExperiences.length > 0 && (
      <div style={{ marginTop: 8 }}>
        {resumeData.workExperiences.map((exp, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{exp.jobTitle}</div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>{exp.companyName} | {exp.startDate} - {exp.endDate || 'Present'}</div>
            {exp.description && (
              <ul style={{ margin: '6px 0 0 18px', fontSize: 15 }}>
                {exp.description.split('\n').map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            )}
          </div>
        ))}
            </div>
    )
  );

  // Helper for rendering projects (only selected)
  const renderProjects = () => {
    if (!resumeData.projectIds || !Array.isArray(resumeData.projectIds)) return null;
    const selectedProjects = projects.filter(p => resumeData.projectIds.includes(p.id));
    if (!selectedProjects.length) return null;
    return (
      <div style={{ marginTop: 8 }}>
        {selectedProjects.map((project, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{project.name}</div>
            {project.client && (
              <div style={{ fontSize: 14, fontWeight: 500, color: '#444', marginBottom: 2 }}>
                Client: {project.client}
              </div>
            )}
            {project.domain && (
              <div style={{ fontSize: 14, fontWeight: 500, color: '#444', marginBottom: 2 }}>
                Domain: {project.domain}
              </div>
            )}
            {/* Project Description: HTML or bullets/paragraph */}
            {project.description && (() => {
              const desc = project.description.trim();
              if (/<ul>|<li>|<ol>/.test(desc)) {
                return <div style={{ fontSize: 15 }} dangerouslySetInnerHTML={{ __html: desc }} />;
              }
              const lines = desc.split(/\r?\n|•/).map(l => l.trim()).filter(Boolean);
              if (lines.length > 1) {
                return (
                  <ul style={{ margin: '6px 0 0 18px', fontSize: 15 }}>
                    {lines.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                );
              }
              return <div style={{ fontSize: 15 }}>{desc}</div>;
            })()}
            {/* Responsibilities: HTML or bullets/paragraph, as sub-section */}
            {project.responsibilities && (() => {
              const resp = project.responsibilities.trim();
              if (!resp) return null;
              return (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>Responsibilities:</div>
                  {/<ul>|<li>|<ol>/.test(resp) ? (
                    <div style={{ fontSize: 15 }} dangerouslySetInnerHTML={{ __html: resp }} />
                  ) : (() => {
                    const lines = resp.split(/\r?\n|•/).map(l => l.trim()).filter(Boolean);
                    if (lines.length > 0) {
                      return (
                        <ul style={{ margin: '6px 0 0 18px', fontSize: 15 }}>
                          {lines.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                      );
                    }
                    return <div style={{ fontSize: 15 }}>{resp}</div>;
                  })()}
            </div>
              );
            })()}
          </div>
        ))}
      </div>
    );
  };

  // Helper for rendering education
  const renderEducation = () => (
    resumeData.educations && resumeData.educations.length > 0 && (
      <div style={{ marginTop: 8 }}>
        {resumeData.educations.map((edu, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{edu.degree}</div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>{edu.institution} | {edu.endDate}</div>
          </div>
        ))}
            </div>
    )
  );

  // Helper for rendering certifications
  const renderCertifications = () => (
    certifications && certifications.length > 0 && (
      <div style={{ marginTop: 8 }}>
        {certifications.map((cert, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{cert.name}</div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>{cert.issuingOrganization} | {cert.issueDate} - {cert.expiryDate || 'Present'}</div>
            {cert.description && <div style={{ fontSize: 15 }}>{cert.description}</div>}
                  </div>
                ))}
              </div>
    )
  );

  return (
                    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      padding: '40px 0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }}>
      {/* Buttons Row */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 24 }}>
        {onDownloadPDF && (
          <button
            onClick={onDownloadPDF}
            style={{
              background: '#22223b',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 28px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 17,
              boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
              letterSpacing: '0.5px',
            }}
          >
            ⬇️ Download as PDF
          </button>
        )}
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: '#fff',
              color: '#22223b',
              border: '2px solid #22223b',
              borderRadius: 6,
              padding: '10px 28px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 17,
              boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
              letterSpacing: '0.5px',
            }}
          >
            ← Back to Builder
          </button>
                      )}
                    </div>
      {/* Resume Preview Card */}
      <div style={{
        background: '#fff',
        borderRadius: 18,
        boxShadow: '0 6px 32px rgba(80,80,120,0.10)',
        padding: '40px 32px 32px 32px',
        maxWidth: 700,
        width: '100%',
        margin: '0 auto',
        minHeight: 600,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Name and Contact */}
        <div style={{ width: '100%', marginBottom: 8 }}>
          <div style={{ fontWeight: 900, fontSize: 28, letterSpacing: 0.5 }}>{resumeData.name}</div>
          <div style={{ marginTop: 2 }}>{renderContactLinks()}</div>
                  </div>
        <hr style={{ width: '100%', border: 'none', borderTop: '1.5px solid #bbb', margin: '18px 0 12px 0' }} />
        {/* Profile Summary */}
        {resumeData.profileSummary && (
          <div style={{ width: '100%', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Profile Summary</div>
            {renderProfileSummary()}
            </div>
          )}
        <hr style={{ width: '100%', border: 'none', borderTop: '1.5px solid #bbb', margin: '18px 0 12px 0' }} />
        {/* Technical Skills */}
        {technicalSkills.length > 0 && (
          <div style={{ width: '100%', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Technical Skills</div>
            {renderSkills()}
                        </div>
                      )}
        <hr style={{ width: '100%', border: 'none', borderTop: '1.5px solid #bbb', margin: '18px 0 12px 0' }} />
        {/* Experience */}
        {renderExperience() && (
          <div style={{ width: '100%', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Professional Experience</div>
            {renderExperience()}
                        </div>
                      )}
        <hr style={{ width: '100%', border: 'none', borderTop: '1.5px solid #bbb', margin: '18px 0 12px 0' }} />
        {/* Projects */}
        {renderProjects() && (
          <div style={{ width: '100%', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Projects</div>
            {renderProjects()}
            </div>
          )}
        <hr style={{ width: '100%', border: 'none', borderTop: '1.5px solid #bbb', margin: '18px 0 12px 0' }} />
          {/* Education */}
        {renderEducation() && (
          <div style={{ width: '100%', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Education</div>
            {renderEducation()}
            </div>
          )}
          {/* Certifications */}
        {renderCertifications() && (
          <>
            <hr style={{ width: '100%', border: 'none', borderTop: '1.5px solid #bbb', margin: '18px 0 12px 0' }} />
            <div style={{ width: '100%', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>Certifications</div>
              {renderCertifications()}
            </div>
          </>
          )}
      </div>
    </div>
  );
};

export default ResumePreview; 